import React from 'react';
import { useQuery } from 'react-query';

import styled from 'styled-components';

import { getCommunity, getCompetitors } from 'apis/CommunityAPI';
import { getApartmentTypes } from 'apis/SettingsAPI';
import { WIDGET_TYPES_LABELS } from 'common/Widgets/constants';
import Widget from 'common/Widgets/Widget/Widget';
import Button from 'components/lib/Button';
import { CommunityViewContext } from 'components/View/CommunityViewContext';
import { getWidgetFilter } from 'components/View/utils';
import { SelectAdvancedFilterOptionType } from 'types/common';
import { RecommendationCommunityFilterType, RecommendationCommunityOverviewType } from 'types/recommendations';

import RecommendationCommunityFilters from './RecommendationCommunityFilters';
import useRecommendationsContext from './RecommendationsContext';

const Menu = styled.div`
  display: flex;
  gap: 10px;
  justify-content: center;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;

const WidgetContainer = styled.div`
  max-width: 100%;
  display: flex;
  flex-direction: column;
  width: fit-content;
  .widget-container {
    &.amenities {
      width: 100%;
      height: 685px;
      max-width: 1284px;
      th {
        &:nth-last-of-type(1) {
          .column-header-label {
            max-width: 104px;
          }
        }
      }
    }
  }
`;

type Props = {
  communityId: number;
  data: RecommendationCommunityOverviewType;
  widget: number;
  availableWidgets: number[];
  filters: RecommendationCommunityFilterType;
  onWidgetChange: (widget: number) => void;
  onFilterChange: (filter: Record<string, any>) => void;
};

const RecommendationCommunityWidgets = ({
  communityId,
  data,
  widget,
  availableWidgets,
  filters,
  onWidgetChange,
  onFilterChange,
}: Props) => {
  const { careTypeOptions } = useRecommendationsContext();

  const handleWidgetChange = (widget: number) => () => {
    onWidgetChange(widget);
  };

  const notImplemented = (name: string) => () => {
    console.log(`Not implemented: ${name}`);
  };

  const { data: community } = useQuery({
    queryKey: ['community', communityId],
    queryFn: () => getCommunity(communityId),
    refetchOnWindowFocus: false,
    enabled: Boolean(communityId),
  });

  const { data: competitors = [], isFetched: isCompetitorsFetched } = useQuery({
    queryKey: ['competitors', communityId],
    queryFn: () => getCompetitors(communityId),
    refetchOnWindowFocus: false,
    enabled: Boolean(communityId),
  });

  const { data: apartmentTypes = [] } = useQuery({
    queryKey: ['apartmentTypes', community, competitors],
    queryFn: () => {
      return getApartmentTypes({ communities: [communityId, ...(competitors ?? []).map((item: any) => item.id)] });
    },
    refetchOnWindowFocus: false,
    enabled: Boolean(communityId),
  });

  const unitTypeOptions: SelectAdvancedFilterOptionType[] = apartmentTypes?.map((item: any) => ({
    label: item.type,
    value: item.id,
  }));

  return (
    <Container>
      <Menu>
        {availableWidgets.map((w) => (
          <Button
            key={w}
            type={w === widget ? 'primary' : 'default'}
            size="small"
            shape="round"
            onClick={handleWidgetChange(w)}
          >
            {WIDGET_TYPES_LABELS[w]}
          </Button>
        ))}
      </Menu>
      <CommunityViewContext.Provider
        value={{
          contentRef: undefined,
          viewId: 0,
          view: undefined,
          viewFilters: undefined,
          filtersModified: false,
          communityId: communityId,
          community: community,
          competitorId: undefined,
          competitors: competitors ?? [],
          isCompetitorsFetched,
          apartmentTypes,
          setView: notImplemented('setView'),
          showViewModal: '',
          isViewCMA: false,
          isViewSalesBattlecard: false,
          setShowViewModal: notImplemented('setShowViewModal'),
          setViewFilters: notImplemented('setViewFilters'),
          viewRefetch: notImplemented('viewRefetch'),
          setCommunityId: notImplemented('setCommunityId'),
          triggerExpandAllRef: {
            current: () => {
              // Do Nothing
            },
          },
          triggerCollapseAllRef: {
            current: () => {
              // Do Nothing
            },
          },
          preview: false,
        }}
      >
        <WidgetContainer>
          <RecommendationCommunityFilters
            filters={filters}
            careTypeOptions={careTypeOptions}
            unitTypeOptions={unitTypeOptions ?? []}
            onFiltersChange={onFilterChange}
            widget={widget}
          />

          <Widget
            name={WIDGET_TYPES_LABELS[widget]}
            type={widget}
            note=""
            filters={getWidgetFilter(
              true,
              {
                careTypes: filters.careTypes,
                roomTypes: filters.unitTypes,
                competitors: filters.competitors,
                careLevels: filters.careLevels,
                reviewSources: filters.reviewSources,
                reviewCategories: filters.reviewCategories,
                swotCategories: filters.swotCategories,
              },
              null
            )}
          />
        </WidgetContainer>
      </CommunityViewContext.Provider>
    </Container>
  );
};

export default RecommendationCommunityWidgets;
